/**
 * PIEDisplayMode — context + helpers that let Mirror switch between
 * raw numbers and verbiage labels (e.g. "On Track" instead of "72%")
 * based on the active org's preference.
 *
 * Owned by the Mirror surface only — Mission Control always renders
 * raw numbers because the admin audience needs precision.
 *
 * The provider lives just inside PIELayout so a single fetch covers
 * every child route. The mode falls back to "numbers" with the
 * default 4-tier scale if anything fails, so a network blip never
 * leaves the UI unable to render a value.
 */
import React, { createContext, useContext, useEffect, useState, useMemo, useCallback } from 'react';
import api from '../../lib/api';
import { toast } from 'sonner';
import logger from '../../lib/logger';

const DEFAULT_THRESHOLDS = [
  { min: 0, max: 39, label: 'Needs Focus' },
  { min: 40, max: 59, label: 'Building' },
  { min: 60, max: 79, label: 'On Track' },
  { min: 80, max: 100, label: 'Excelling' },
];

const DEFAULT_TREND_LABELS = {
  improving: 'Improving',
  steady: 'Steady',
  slipping: 'Slipping',
};

const DEFAULT_CONTEXT = {
  orgId: null,
  orgName: null,
  updatedAt: null,
  updatedBy: null,
  mode: 'numbers',
  thresholds: DEFAULT_THRESHOLDS,
  trendLabels: DEFAULT_TREND_LABELS,
  loaded: false,
  setMode: async () => {},
  refresh: async () => {},
};

const PIEDisplayContext = createContext(DEFAULT_CONTEXT);

function _readViewAsOrgId() {
  try {
    const raw = sessionStorage.getItem('viewAsOrg');
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    return parsed?.id || null;
  } catch (e) {
    return null;
  }
}

export function PIEDisplayModeProvider({ children }) {
  const [state, setState] = useState(DEFAULT_CONTEXT);

  const applyData = useCallback((data) => {
    setState((s) => ({
      ...s,
      orgId: data?.org_id || null,
      orgName: data?.org_name || null,
      updatedAt: data?.updated_at || null,
      updatedBy: data?.updated_by || null,
      mode: data?.mode === 'verbiage' ? 'verbiage' : 'numbers',
      thresholds: Array.isArray(data?.thresholds) && data.thresholds.length
        ? data.thresholds
        : DEFAULT_THRESHOLDS,
      trendLabels: { ...DEFAULT_TREND_LABELS, ...(data?.trend_labels || {}) },
      loaded: true,
    }));
  }, []);

  const refresh = useCallback(async () => {
    try {
      // Super admins may have a "Switch Org" override stored in
      // session. Honour it so the toggle/preview operate on the org
      // they're currently demoing rather than their own (often empty)
      // primary org membership.
      const overrideOrgId = _readViewAsOrgId();
      const url = overrideOrgId
        ? `/personal/display-mode?org_id=${encodeURIComponent(overrideOrgId)}`
        : '/personal/display-mode';
      const r = await api.get(url);
      applyData(r.data);
      return r.data;
    } catch (e) {
      logger.warn('display-mode probe failed; falling back to numbers', e);
      setState((s) => ({ ...s, loaded: true }));
      return null;
    }
  }, [applyData]);

  const setMode = useCallback(async (nextMode) => {
    // Optimistic local flip so the Mirror surface reacts immediately,
    // then persist. Roll back on server failure.
    const target = state.orgId || _readViewAsOrgId();
    if (!target) {
      toast.error('No organization linked to this account — cannot save display mode.');
      return false;
    }
    let priorMode;
    setState((s) => {
      priorMode = s.mode;
      return { ...s, mode: nextMode };
    });
    try {
      const r = await api.put(`/personal/display-mode/${target}`, { mode: nextMode });
      applyData(r.data);
      toast.success(
        nextMode === 'verbiage'
          ? 'Mirror now showing verbiage for this org'
          : 'Mirror now showing raw numbers for this org',
      );
      return true;
    } catch (e) {
      logger.error('Failed to persist display mode flip', e);
      toast.error(e?.response?.data?.detail || 'Could not save display mode');
      setState((s) => ({ ...s, mode: priorMode }));
      return false;
    }
  }, [state.orgId, applyData]);

  useEffect(() => {
    let mounted = true;
    const overrideOrgId = _readViewAsOrgId();
    const url = overrideOrgId
      ? `/personal/display-mode?org_id=${encodeURIComponent(overrideOrgId)}`
      : '/personal/display-mode';
    api
      .get(url)
      .then((r) => {
        if (!mounted) return;
        applyData(r.data);
      })
      .catch((e) => {
        logger.warn('display-mode probe failed; falling back to numbers', e);
        setState((s) => ({ ...s, loaded: true }));
      });
    return () => { mounted = false; };
  }, [applyData]);

  const value = useMemo(
    () => ({ ...state, setMode, refresh }),
    [state, setMode, refresh],
  );

  return (
    <PIEDisplayContext.Provider value={value}>
      {children}
    </PIEDisplayContext.Provider>
  );
}

export function usePIEDisplayMode() {
  return useContext(PIEDisplayContext);
}

/**
 * Convert a numeric value to a verbiage label using the configured
 * thresholds. Accepts 0–1, 0–100, or 1–5 — auto-detected by the
 * `kind` arg so we never have to thread it through every call site.
 */
export function valueToBucket(value, thresholds, kind = 'pct01') {
  if (value === null || value === undefined || Number.isNaN(Number(value))) {
    return null;
  }
  const v = Number(value);
  let norm;
  if (kind === 'pct01') norm = v * 100;
  else if (kind === 'pct100') norm = v;
  else if (kind === 'pulse5') norm = ((v - 1) / 4) * 100; // 1..5 → 0..100
  else norm = v;
  norm = Math.max(0, Math.min(100, norm));
  const tier = (thresholds || DEFAULT_THRESHOLDS).find(
    (t) => norm >= t.min && norm <= t.max,
  );
  return tier?.label || null;
}

/**
 * Convert a signed *delta* to a trend label.
 *  > +2 pts  → improving
 *  ≥ −2 pts → steady
 *  else     → slipping
 * Threshold lives in display-mode space (percentage points) to match
 * how managers read deltas.
 */
export function deltaToTrendLabel(delta, trendLabels, kind = 'pct01') {
  if (delta === null || delta === undefined || Number.isNaN(Number(delta))) {
    return null;
  }
  const d = Number(delta);
  const pts = kind === 'pct01' ? d * 100 : d; // already in pp space otherwise
  if (pts > 2) return trendLabels?.improving || 'Improving';
  if (pts < -2) return trendLabels?.slipping || 'Slipping';
  return trendLabels?.steady || 'Steady';
}

/**
 * <Perf /> — drop-in replacement for `pct(value)` style calls. Renders
 * a percentage in "numbers" mode and a verbiage chip in "verbiage" mode.
 *
 * Usage:
 *   <Perf value={0.72} />                  → "72%" or "On Track"
 *   <Perf value={72} kind="pct100" />      → same, 0–100 input
 *   <Perf value={3.4} kind="pulse5" />     → 1–5 pulse input
 *   <Perf value={0.025} kind="delta01" />  → "+2.5 pts" or "Improving"
 */
export function Perf({ value, kind = 'pct01', digits = 0, fallback = '—', signed = true, className, style, testid }) {
  const { mode, thresholds, trendLabels } = usePIEDisplayMode();
  if (value === null || value === undefined || Number.isNaN(Number(value))) {
    return <span className={className} style={style} data-testid={testid}>{fallback}</span>;
  }

  if (mode === 'verbiage') {
    let label;
    if (kind === 'delta01' || kind === 'delta100') {
      label = deltaToTrendLabel(value, trendLabels, kind === 'delta01' ? 'pct01' : 'pct100');
    } else {
      label = valueToBucket(value, thresholds, kind);
    }
    return (
      <span
        className={className}
        style={style}
        data-testid={testid}
        data-display-mode="verbiage"
      >
        {label || fallback}
      </span>
    );
  }

  // numbers mode: original numeric formatting
  const v = Number(value);
  let text;
  if (kind === 'pct01') text = `${(v * 100).toFixed(digits)}%`;
  else if (kind === 'pct100') text = `${v.toFixed(digits)}%`;
  else if (kind === 'pulse5') text = v.toFixed(digits || 1);
  else if (kind === 'delta01') {
    const pp = v * 100;
    const sign = signed ? (pp >= 0 ? '+' : '') : '';
    text = `${sign}${Math.abs(pp).toFixed(digits || 1)} pts`;
    if (signed && pp < 0) text = `-${Math.abs(pp).toFixed(digits || 1)} pts`;
  } else if (kind === 'delta100') {
    const sign = signed ? (v >= 0 ? '+' : '') : '';
    text = `${sign}${Math.abs(v).toFixed(digits || 1)} pts`;
    if (signed && v < 0) text = `-${Math.abs(v).toFixed(digits || 1)} pts`;
  } else {
    text = String(v);
  }
  return (
    <span
      className={className}
      style={style}
      data-testid={testid}
      data-display-mode="numbers"
    >
      {text}
    </span>
  );
}
